import { Box, Divider, Stack } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { VirtualItem } from "@tanstack/react-virtual";
import { memo, useContext, useMemo } from "react";
import { WorkspaceContext } from "../../../contexts/WorkspaceContext";
import useBackwardsCompatible from "../../../hooks/useBackwardsCompatible";
import useDashboardsTable from "../../../hooks/useDashboardsTable";
import useVirtualTable from "../../../hooks/useVirtualTable";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Workspace } from "../../../types/files";
import { collectionKey } from "../../organisms/dashboards/DashboardsView";
import { FallbackLoading } from "../../templates/loader";
import NoDashboardsFoundView from "../dashboards-empty/NoDashboardsFoundView";
import DashboardsTableHeader from "../dashboards-table/DashboardsTableHeader";
import DashboardsTableRow from "../dashboards-table/DashboardsTableRow";

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    headers: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    //
    table: {
        width: '100%',
        height: 'calc(100vh - 240px)',
    },
    header: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'auto',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        ...scrollbarStyle,
    },
    rows: {
        position: 'relative',
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    row: {
        position: 'absolute',
        top: 0,
        left: 0,
        minWidth: '100%',
        width: 'fit-content',
        height: 'auto',
        borderTop: `1px solid ${theme.colors.neutral['400']}`,
        background: `#fff`,
        "&:hover": {
            background: `rgb(246, 251, 251, 0.8)`,
        },
    },
}));

const TableHeaders = ['Name', 'Stage', 'Sectors', 'Location', '', 'Added', ''];

const SharedByDealOwner: React.FC<{
    workspace?: Workspace,
    search?: string,
}> = ({ workspace, search }) => {
    const classes = useStyles();
    const { workspace: globalWorkspace } = useContext(WorkspaceContext);
    const { dashboardSource } = useBackwardsCompatible();

    const localWorkspace = useMemo(() => workspace ?? globalWorkspace, [globalWorkspace, workspace]);

    const { loading, rows, filters, handleFilterRows } = useDashboardsTable(localWorkspace?.id ?? undefined, search);

    const filteredRows = useMemo(() => rows.filter(dashboard => {
            const source = dashboardSource(dashboard);

            return (dashboard.source?.toLowerCase() === localWorkspace?.name?.toLowerCase()
                || (localWorkspace?.name?.toLowerCase() === source?.name?.toLowerCase()
                    && localWorkspace?.email?.toLowerCase() === source?.email?.toLowerCase()));
        }).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    // eslint-disable-next-line
    , [localWorkspace, rows]);

    const { allRows, rowVirtualizer, hasNextPage, virtualRowsRef, virtualHeaderRef }
        = useVirtualTable({ key: 'shared-by-deal-owner-138', source: filteredRows, limit: 20, itemHeight: 60 });

    if (!workspace)
        return (<></>);

    return (<>
        <Stack className={classes.container} alignItems="space-between" justifyContent="stretch">
            {loading ? (<FallbackLoading />) : (<>
                <Stack alignItems="flex-start" justifyContent="flex-start" height="100%">
                    <Box width="100%" height="fit-content" overflow="hidden">
                        <Box className={classes.header} ref={virtualHeaderRef}>
                            <DashboardsTableHeader
                                columns={TableHeaders}
                                filters={filters}
                                actions={['Delete']}
                                onFilter={handleFilterRows}
                                xAdjust={80}
                                multiAction />
                        </Box>
                    </Box>
                    {!!filteredRows?.length ? (<>
                        <Box width="100%" height="100%" overflow="hidden">
                            <Box className={classes.rows} ref={virtualRowsRef}
                                sx={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                                {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => (
                                    <Box className={classes.row}
                                        sx={{  height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)`, }}
                                        key={'shared-by-deal-owner-192-' + virtualRow.index} >
                                        {(virtualRow.index > allRows.length - 1) && hasNextPage
                                            ? (<FallbackLoading />)
                                            : (<DashboardsTableRow
                                                    dashboard={allRows[virtualRow.index]}
                                                    workspace={workspace ?? undefined}
                                                    columns={TableHeaders}
                                                    collectionKey={collectionKey}
                                                    isPublic />)}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </>) : (
                        <Stack alignItems="flex-start" justifyContent="flex-start" width="100%" height="100%">
                            <Divider sx={{ width: '100%' }} />
                            <Stack className={classes.rows} alignItems="flex-start" justifyContent="flex-start">
                                <NoDashboardsFoundView noCompaniesYet />
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </>)}
        </Stack>
    </>);
}

export default memo(SharedByDealOwner);